import React from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FileText, Eye, AlertTriangle, FileClock } from 'lucide-react';

import IconButton from '../ui/IconButton';
import Tooltip from '../ui/Tooltip';
import { formatDate } from '../../utils/Utils';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.metrics.radius.xl};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 5px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// Dosyası olmayan satırın yer tutucusu. IconButton ile AYNI kutu ölçüsünde ki satırlar
// birbirine göre kaymasın; ama tıklanabilir görünmesin diye ne kenarlığı ne de hover'ı var.
const NoFile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.dark.little};
  opacity: 0.55;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 16px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// Rozet fonu, theme'in düz rengini saydamlaştırılmış bir tona çevirir — hex literalleri
// (theme token'larının KENDİSİ dışında) hardcode etmemek için.
function tint(hex, alpha) {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const value = parseInt(full, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function PersonnelFileView({ items, fetching, onOpenFile, renderRowAction }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const list = Array.isArray(items) ? items : [];

  if (fetching) return <Empty>{t('common.loading')}</Empty>;
  if (list.length === 0) return <Empty>{t('personnelFile.empty')}</Empty>;

  const sourceColor = {
    ats: theme.colors.auxiliar.info,
    preboarding: theme.colors.auxiliar.warning,
    employment: theme.colors.secondary.dark,
    certification: theme.colors.text.dark.little,
    offboarding: theme.colors.auxiliar.danger,
  };

  return (
    <Wrap>
      {list.map((item) => {
        const color = sourceColor[item.source] || theme.colors.text.dark.little;
        const extra = renderRowAction ? renderRowAction(item) : null;
        return (
          <Row key={`${item.source}-${item.id}`}>
            <FileText size={18} color={color} />
            <Body>
              <Title>{t(`personnelFile.type.${item.documentType}`, { defaultValue: item.title })}</Title>
              <Meta>
                <Badge $bg={tint(color, 0.12)} $color={color}>
                  {t(`personnelFile.source.${item.source}`)}
                </Badge>
                <span>{t(`personnelFile.status.${item.status}`, { defaultValue: item.status })}</span>
                {item.isMandatory && <span>{t('personnelFile.mandatory')}</span>}
                {/* Geçerlilik tarihi spec §8.1'in satır içeriği listesinde yer alır; süreli
                    belgelerde (kimlik, çalışma izni, sertifika) taşınan TEK tarih budur.
                    Yalnız dolu olduğunda gösterilir — süresiz belgelerde alan null gelir. */}
                {item.validUntil && (
                  <span>
                    {t('personnelFile.validUntil', { date: formatDate(item.validUntil, i18n.language) })}
                  </span>
                )}
                {item.isExpired && (
                  <Badge $bg={tint(theme.colors.auxiliar.danger, 0.12)} $color={theme.colors.auxiliar.danger}>
                    <AlertTriangle size={12} />
                    {t('personnelFile.expired')}
                  </Badge>
                )}
              </Meta>
            </Body>
            {extra}
            {item.hasFile ? (
              // `Eye`, ExternalLink DEĞİL: bu aksiyon artık DocumentViewerModal'ı açıyor
              // (ürün sahibi kararı — belgeler ASLA yeni sekmede açılmaz), ExternalLink ikonu
              // tam da reddedilen davranışı ("yeni sekmede açılır") vaat ediyordu.
              <Tooltip text={t('personnelFile.openFile')}>
                <IconButton onClick={() => onOpenFile(item)}>
                  <Eye size={16} />
                </IconButton>
              </Tooltip>
            ) : (
              // Dosyası olmayan satırda daha önce HİÇBİR ŞEY yoktu; kullanıcı boşluğu görüp
              // "yetkim yok galiba" diye yorumluyordu (müşteri bildirimi 2026-08-18). Satır
              // aslında bir TALEP: İK istemiş, henüz yüklenmemiş. Soluk ikon + ipucu bunu
              // söylüyor; aksiyon değil, durum göstergesi olduğu için IconButton DEĞİL.
              <Tooltip text={t('personnelFile.notUploadedHint')}>
                <NoFile data-testid="no-file-indicator">
                  <FileClock size={16} />
                </NoFile>
              </Tooltip>
            )}
          </Row>
        );
      })}
    </Wrap>
  );
}
